import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validators,
  Validator,
} from '@angular/forms';
import { PasscodeDigit } from './../../services/authentication.service';
import {
  LoggedOutEntitlement,
  LoggedOutEntitlementsService,
} from '../../services/logged-out-entitlements.service';

@Component({
  selector: 'passcode-input',
  templateUrl: './passcode.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => PasscodeComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      // eslint-disable-next-line no-use-before-define
      useExisting: PasscodeComponent,
    },
  ],
})
export class PasscodeComponent implements OnInit, Validator {
  @Input() passcodeInputs = [true, true, true, true, true, true];
  @Input() labelPrefix: string;
  @Input() ariaDescribedBy: string;
  @Input() isToggleable: boolean = false;
  @Input() checkPasscode: boolean = false;
  @Input() validationErrorPrefix: string = 'Passcode';
  @Input() disabled: boolean = false;
  @Input() inputTabindex: number;
  @ViewChildren('input') inputs: QueryList<ElementRef>;

  public _name: string = '';
  @Input()
  set name(value: string) {
    if (value.length === 0) return;

    this._name = value + '-';
  }
  get name(): string {
    return this._name;
  }

  @Output() onInit = new EventEmitter();
  @Output() onUpdate = new EventEmitter();
  @Output() onChangePasscodeVisibility = new EventEmitter();

  public loading: boolean = true;
  public passcodeFields: Array<PasscodeField> = [];
  public passcodeGroup: FormGroup;
  public passcode: Array<number>;
  public valid: boolean = true;
  public inputted: boolean = false;
  public inputHidden: boolean = true;
  public focusedFieldIndex: number = null;
  private control: AbstractControl;
  private onChange: Function = Function.prototype;
  public scaEnabled = false;
  public loadingEntitlements = true;

  constructor(
    private loggedOutEntitlementsService: LoggedOutEntitlementsService
  ) {}

  ngOnInit() {
    this.bindFields();
    this.initPasscodeFields();
    this.onInit.emit(this.passcodeGroup);
    this.loadEntitlements();
  }

  private async loadEntitlements() {
    this.scaEnabled = await this.loggedOutEntitlementsService.hasEntitlement(
      LoggedOutEntitlement.strongCustomerAuthentication
    );
    this.loadingEntitlements = false;
  }

  validate(c: AbstractControl): { [key: string]: any } {
    this.control = c;

    if (!this.valid) {
      return {
        passcode: true,
      };
    }
  }

  writeValue(value: string): void {}

  registerOnChange(fn: (_: any) => {}): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {}

  formatAndPropagateControlValue(): void {
    const passcodeDigits: PasscodeDigit[] = [];

    this.passcode.forEach((digit, position) => {
      if (digit !== null) {
        passcodeDigits.push({
          digit,
          position,
        });
      }
    });

    this.onChange(passcodeDigits);
  }

  public setFocus(): void {
    if (this.inputs == null || this.inputs.length == 0) return;

    let first = true;
    this.inputs.forEach((item) => {
      if (!first) return;

      first = false;
      item.nativeElement.focus();
    });
  }

  public reset(): void {
    this.valid = true;
    this.inputHidden = true;
    this.initPasscodeFields();

    for (let i = 0; i < this.passcodeFields.length; i++)
      this.passcodeFields[i].reset(this.passcodeGroup);
  }

  public changePasscodeVisibility(isHidden: boolean): void {
    this.inputHidden = isHidden;
    this.updatePasscodeFields();
    this.onChangePasscodeVisibility.emit(isHidden);
  }

  public onFocus(index: number): void {
    this.focusedFieldIndex = index;
    this.updatePasscodeFields();
    this.setPasscodeFieldHighlighted(index, true);
  }

  public onBlur(index: number): void {
    if (this.focusedFieldIndex == index) this.focusedFieldIndex = null;

    this.updatePasscodeFields();
    this.setPasscodeFieldHighlighted(index, false);
  }

  public onKeyUp(e: KeyboardEvent, index: number, fieldId: string): void {
    this.restrictInputLength(index, e.target as HTMLInputElement);
    this.update();
    this.updatePasscodeField(index);
    this.focusNextInput(e.keyCode, fieldId);
  }

  public onKeyDown(e: KeyboardEvent, index: number): void {
    const specialKeys = [
      59, 61, 106, 107, 109, 110, 111, 163, 173, 186, 187, 188, 189, 190, 191,
      192, 219, 220, 221, 222,
    ];
    const isSpecialKey = specialKeys.indexOf(e.keyCode) > -1;
    const isAlphaKey = e.keyCode >= 65 && e.keyCode <= 90;
    const isNumericKey = this.isNumericKey(e.keyCode);

    this.updatePasscodeField(index);

    // block invalid keys
    if (isAlphaKey || isSpecialKey || (e.shiftKey && isNumericKey)) {
      e.preventDefault();
      return;
    }

    // control number input length on keyup
    if (!this.inputHidden) return;

    // delete key
    if (e.keyCode == 8) {
      this.setPasscodeFieldValue(index, '');
      return;
    }

    // single number input
    const field = e.target as HTMLInputElement;
    if (
      field == null ||
      field.value == null ||
      field.value.length == 0 ||
      !isNumericKey
    )
      return;

    // if highlighted clear value
    if (this.isPasscodeFieldHighlighted(index)) {
      this.setPasscodeFieldValue(index, '');
      return;
    }

    e.preventDefault();
  }

  public hasErrorInAnyField(): boolean {
    return this.passcodeFields.some(
      (passcode: PasscodeField) => passcode.invalid
    );
  }

  public onPaste(e: any): void {
    e.preventDefault();
  }

  public submit(): void {
    for (let i = 0; i < this.passcodeFields.length; i++)
      this.passcodeFields[i].markDirty(this.passcodeGroup);

    this.updatePasscodeFields();
    this.update();

    if (this.control) {
      this.control.updateValueAndValidity();
    }
  }

  public passcodeAsString(): string {
    if (this.passcode == undefined || this.passcode.length === 0) return '';

    return this.passcode.join('');
  }

  /* -- private ------ */
  private initPasscodeFields(): void {
    if (this.passcodeInputs == null || this.passcodeInputs.length == 0) {
      this.passcodeFields = new Array<PasscodeField>();
      return;
    }

    const fields = new Array<PasscodeField>();
    let inputAdded = false;
    for (let i = 0; i < this.passcodeInputs.length; i++) {
      const firstInput = !inputAdded && this.passcodeInputs[i];
      if (firstInput) inputAdded = true;

      fields.push(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        new PasscodeField(
          i,
          this.passcodeInputs[i],
          this.name,
          this.labelPrefix,
          firstInput
        )
      );
    }

    this.passcodeFields = fields;
  }

  private bindFields(): void {
    const groupConfig = {};
    groupConfig[this.name + 'passcodedigit1'] = new FormControl('', [
      Validators.required,
      Validators.pattern('[0-9]'),
    ]);
    groupConfig[this.name + 'passcodedigit2'] = new FormControl('', [
      Validators.required,
      Validators.pattern('[0-9]'),
    ]);
    groupConfig[this.name + 'passcodedigit3'] = new FormControl('', [
      Validators.required,
      Validators.pattern('[0-9]'),
    ]);
    groupConfig[this.name + 'passcodedigit4'] = new FormControl('', [
      Validators.required,
      Validators.pattern('[0-9]'),
    ]);
    groupConfig[this.name + 'passcodedigit5'] = new FormControl('', [
      Validators.required,
      Validators.pattern('[0-9]'),
    ]);
    groupConfig[this.name + 'passcodedigit6'] = new FormControl('', [
      Validators.required,
      Validators.pattern('[0-9]'),
    ]);

    this.passcodeGroup = new FormGroup(groupConfig);
  }

  private focusNextInput(keyCode: number, fieldId: string): void {
    if (this.inputs == null || this.inputs.length == 0) return;

    if (!this.isNumericKey(keyCode)) return;

    let focusNext = false;
    this.inputs.forEach((item) => {
      if (focusNext) {
        item.nativeElement.focus();
        focusNext = false;
      }

      if (
        item.nativeElement.value.length > 0 &&
        item.nativeElement.getAttribute('name') == fieldId
      )
        focusNext = true;
    });
  }

  private update(): void {
    let valid = true;
    let inputted = true;
    const passcode = new Array<number>();

    for (let i = 0; i < this.passcodeFields.length; i++) {
      if (!this.passcodeFields[i].input) passcode.push(null);
      else {
        const digit = Number(
          this.passcodeGroup.controls[this.passcodeFields[i].id].value
        );
        if (digit != NaN) passcode.push(digit);
        else passcode.push(null);

        const control = this.passcodeGroup.controls[this.passcodeFields[i].id];
        if (control.value === '')
          // && (control.dirty || control.touched))
          inputted = false;

        if (!control.valid && (control.dirty || control.touched)) valid = false;
      }
    }

    this.passcode = passcode;
    this.inputted = inputted;
    this.valid = this.checkPasscode
      ? valid && this.checkPasscodeValid(this.passcodeAsString())
      : valid;

    this.onUpdate.emit(null);
  }

  private restrictInputLength(index: number, field: HTMLInputElement): void {
    if (field == null || field.value == null || field.value.length < 2) return;

    this.setPasscodeFieldValue(index, field.value.substring(0, 1));
  }

  private isNumericKey(keyCode: number) {
    return (
      (keyCode >= 48 && keyCode <= 57) ||
      (keyCode >= 96 && keyCode <= 105) ||
      keyCode == 229
    );
  }

  /* -- PasscodeField calls ------ */
  private updatePasscodeFields(): void {
    for (let i = 0; i < this.passcodeFields.length; i++)
      this.updatePasscodeField(i);
  }

  private updatePasscodeField(index: number): void {
    this.passcodeFields[index].update(
      this.passcodeGroup,
      this.focusedFieldIndex
    );
  }

  private setPasscodeFieldValue(index: number, value: string): void {
    this.passcodeFields[index].setValue(this.passcodeGroup, value);
  }

  private setPasscodeFieldHighlighted(index: number, value: boolean): void {
    return this.passcodeFields[index].setHighlighted(value);
  }

  private isPasscodeFieldHighlighted(index: number): boolean {
    return this.passcodeFields[index].highlighted;
  }

  private checkPasscodeValid(passcode: string): boolean {
    // all digits
    const passcodeNum = parseInt('1' + passcode);
    if (
      isNaN(passcodeNum) ||
      passcode.length != passcodeNum.toString().length - 1
    )
      return false;

    // same digit and sequence checks
    let sameDigit = true;
    let isAscSequence = true;
    let isDescSequence = true;
    for (let i = 0; i < passcode.length - 1; i++) {
      if (passcode[i] != passcode[i + 1]) sameDigit = false;

      const ascVal = parseInt(passcode[i]) + 1;
      if (
        ascVal > 9 &&
        parseInt(passcode[i + 1]) != 0 &&
        parseInt(passcode[i + 1]) != 1
      )
        isAscSequence = false;
      else if (ascVal <= 9 && ascVal != parseInt(passcode[i + 1]))
        isAscSequence = false;

      const descVal = parseInt(passcode[i]) - 1;
      if (
        descVal == 0 &&
        parseInt(passcode[i + 1]) != 0 &&
        parseInt(passcode[i + 1]) != 9
      )
        isDescSequence = false;
      else if (descVal == -1 && parseInt(passcode[i + 1]) != 9)
        isDescSequence = false;
      else if (descVal >= 1 && descVal != parseInt(passcode[i + 1]))
        isDescSequence = false;
    }

    return !(sameDigit || isAscSequence || isDescSequence);
  }
}

class PasscodeField {
  index: number;
  input: boolean = true;
  id: string = '';
  label: string = '';
  number: string = '';
  focused: boolean = false;
  inputted: boolean = false;
  highlighted: boolean = false;
  invalid: boolean = false;

  constructor(
    index: number,
    input: boolean,
    idPrefix: string,
    labelPrefix: string,
    firstInput: boolean
  ) {
    this.index = index;
    this.input = input;

    const num = index + 1;
    this.id = idPrefix + 'passcodedigit' + num;
    this.number = this.getNumber(num);

    if (labelPrefix) {
      this.label = labelPrefix;
    } else {
      this.label = 'Enter passcode';
    }

    this.label += ', ' + this.number + ' digit';
  }

  public update(form: FormGroup, focusedIndex: number): void {
    const field = form.controls[this.id];

    this.setFocused(focusedIndex);
    this.setInputted(field);
    this.setInvalid(field);
  }

  public setValue(form: FormGroup, value: string): void {
    this.inputted = value != '';

    const field = form.controls[this.id];
    field.setValue(value);

    this.highlighted = false;
  }

  public setHighlighted(value: boolean): void {
    this.highlighted = value && this.focused && this.inputted;
  }

  public reset(form: FormGroup): void {
    this.focused = false;
    this.inputted = false;
    this.invalid = false;

    if (form == null) return;

    const field = form.controls[this.id];
    field.setValue('');
    field.markAsPristine();
    field.markAsUntouched();
  }

  public markDirty(form: FormGroup): void {
    if (!this.input) return;

    form.controls[this.id].markAsDirty();
  }

  private getNumber(index: number): string {
    let prefix = 'th';

    switch (index) {
      case 1:
        prefix = 'st';
        break;
      case 2:
        prefix = 'nd';
        break;
      case 3:
        prefix = 'rd';
        break;
    }

    return index + prefix;
  }

  private setFocused(focusedIndex: number): void {
    this.focused = this.index == focusedIndex;
  }

  private setInputted(field: AbstractControl): void {
    const value = field.value;
    this.inputted = value != null && value.toString().length > 0;
  }

  private setInvalid(field: AbstractControl): void {
    this.invalid = !field.valid && (field.dirty || field.touched);
  }
}
